import type { FieldBody, FieldFilter, ViewConfig } from '@/shared/api/generated/model';
import { pluralize } from '@/shared/lib/plural';

/**
 * Модель списка записей: настройка вида (как показывать и что отбирать) и
 * правила отображения значений. Сравнения и сортировка — зеркало сервера
 * (internal/modules/records/lists.go: opsFor, sortable).
 */

export const DEFAULT_CONFIG: ViewConfig = { layout: 'table', columns: [], filters: [] };

export type Op = FieldFilter['op'];

export const OP_TITLES: Record<Op, string> = {
  eq: 'равно',
  ne: 'не равно',
  gte: 'не меньше',
  lte: 'не больше',
  contains: 'содержит',
  in: 'любое из',
  filled: 'заполнено',
  empty: 'пусто',
};

/** Сравнения, доступные полю этого вида. */
export function opsFor(type: string): Op[] {
  switch (type) {
    case 'string':
    case 'text':
    case 'url':
      return ['contains', 'eq', 'ne', 'filled', 'empty'];
    case 'richtext':
      return ['contains', 'filled', 'empty'];
    case 'select':
    case 'boolean':
    case 'reference':
    case 'image':
    case 'file':
      return ['eq', 'ne', 'filled', 'empty'];
    case 'number':
    case 'date':
    case 'datetime':
      return ['eq', 'ne', 'gte', 'lte', 'filled', 'empty'];
    default:
      return ['filled', 'empty'];
  }
}

/**
 * Каким фильтром поле показывается в панели фильтров: варианты списка —
 * флажками, ссылка — флажками записей, число и дата — «от и до», текст —
 * «содержит», файлы и группы — «заполнено или пусто». null — поле в панели
 * не показывается.
 */
export type PanelKind =
  'choices' | 'reference' | 'boolean' | 'range' | 'dates' | 'text' | 'presence';

export function panelKind(type: string): PanelKind | null {
  switch (type) {
    case 'select':
      return 'choices';
    case 'reference':
      return 'reference';
    case 'boolean':
      return 'boolean';
    case 'number':
      return 'range';
    case 'date':
    case 'datetime':
      return 'dates';
    case 'string':
    case 'text':
    case 'url':
    case 'richtext':
      return 'text';
    case 'image':
    case 'file':
    case 'group':
    case 'blocks':
      return 'presence';
    default:
      return null;
  }
}

/** Порядок разделов панели: чем чаще ищут — тем выше. */
export const PANEL_ORDER: readonly PanelKind[] = [
  'choices',
  'reference',
  'boolean',
  'range',
  'dates',
  'text',
  'presence',
];

/** Условия одного поля. */
export function filtersOf(filters: readonly FieldFilter[], key: string): FieldFilter[] {
  return filters.filter((item) => item.field === key);
}

/** Заменить условия одного поля, не трогая остальные и их порядок. */
export function withField(
  filters: readonly FieldFilter[],
  key: string,
  next: readonly FieldFilter[],
): FieldFilter[] {
  return [...filters.filter((item) => item.field !== key), ...next];
}

/** Отмеченные значения поля: «любое из» или одно «равно». */
export function chosenOf(filters: readonly FieldFilter[], key: string): unknown[] {
  const found = filtersOf(filters, key).find((item) => item.op === 'in' || item.op === 'eq');

  if (found === undefined) {
    return [];
  }

  return Array.isArray(found.value) ? found.value : [found.value];
}

/** Условие на отмеченные значения: ни одного — условия нет. */
export function chosenFilter(key: string, values: readonly unknown[]): FieldFilter[] {
  return values.length === 0 ? [] : [{ field: key, op: 'in', value: [...values] }];
}

/** Значение условия поля с этим сравнением. */
export function valueOf(filters: readonly FieldFilter[], key: string, op: Op): unknown {
  return filtersOf(filters, key).find((item) => item.op === op)?.value;
}

/** Сколько полей с условиями — число на кнопке «Фильтры». */
export function activeFields(filters: readonly FieldFilter[]): number {
  return new Set(filters.map((item) => item.field)).size;
}

export function needsValue(op: Op): boolean {
  return op !== 'filled' && op !== 'empty';
}

/** По полю этого вида можно сортировать. */
export function sortable(type: string): boolean {
  return ['string', 'text', 'url', 'select', 'number', 'boolean', 'date', 'datetime'].includes(
    type,
  );
}

/** Поля, годные в столбцы и фильтры: верхнего уровня и не скрытые. */
export function listFields(fields: readonly FieldBody[]): FieldBody[] {
  return fields.filter((item) => !item.hidden && item.parent_id === undefined);
}

const dateFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});
const dateTimeFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** Значение поля в ячейке — коротко и словами. */
export function cellText(field: FieldBody, value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  switch (field.type) {
    case 'boolean':
      return value === true ? 'да' : 'нет';
    case 'date':
      return typeof value === 'string' ? dateFormat.format(new Date(value)) : '—';
    case 'datetime':
      return typeof value === 'string' ? dateTimeFormat.format(new Date(value)) : '—';
    case 'group':
    case 'blocks':
      return Array.isArray(value)
        ? pluralize(value.length, ['элемент', 'элемента', 'элементов'])
        : '—';
    case 'image':
    case 'file':
      return 'файл';
    case 'richtext':
      return typeof value === 'string' ? clip(value.replace(/<[^>]*>/g, ' ')) : '—';
    default:
      return typeof value === 'string' || typeof value === 'number' ? clip(String(value)) : '—';
  }
}

function clip(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();

  return clean.length > 80 ? `${clean.slice(0, 80)}…` : clean;
}

/** Совпадают ли две настройки — чтобы показать «вид изменён, сохранить?». */
export function sameConfig(a: ViewConfig, b: ViewConfig): boolean {
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}

function normalize(config: ViewConfig): ViewConfig {
  return {
    layout: config.layout,
    columns: config.columns ?? [],
    filters: config.filters ?? [],
    ...(config.status === undefined ? {} : { status: config.status }),
    ...(config.sort === undefined ? {} : { sort: config.sort }),
    ...(config.order === undefined ? {} : { order: config.order }),
  };
}

/**
 * Выбор строк с Shift, как в медиатеке: от прошлого щелчка (anchor) до
 * этой строки — всё в одно состояние с ней. Без Shift или если прошлой
 * строки на странице нет — только эта строка.
 */
export function selectRange(
  order: readonly string[],
  current: Readonly<Record<string, boolean>>,
  target: string,
  on: boolean,
  anchor: string | undefined,
  range: boolean,
): Record<string, boolean> {
  const from = anchor === undefined ? -1 : order.indexOf(anchor);
  const to = order.indexOf(target);

  if (!range || from < 0 || to < 0) {
    return { ...current, [target]: on };
  }

  const next = { ...current };

  for (const id of order.slice(Math.min(from, to), Math.max(from, to) + 1)) {
    next[id] = on;
  }

  return next;
}
